/**
 * Per-tenant PWA wiring.
 *
 * online-ordering-v2 is a single static bundle that serves many tenants —
 * brand name, logo, and theme color all arrive at runtime from
 * `configApi.get(slug)`. This module updates the document so the browser
 * tab title, web-app manifest link, apple-touch-icon, and theme-color
 * meta all reflect the *active* tenant. Called from `BranchProvider` once
 * the config resolves (and on subsequent tenant changes).
 *
 * Why a backend manifest endpoint instead of a client-built Blob?
 * Chrome's WebAPK Minting Server (which builds the actual installable
 * Android app when a user taps "Install") fetches the manifest from a
 * real HTTPS URL on Google's servers — a `blob:` URL is local to the
 * user's browser session and cannot be reached server-side. Pointing the
 * `<link rel="manifest">` at the API gateway endpoint
 * `/v1/online-ordering/manifest/{slug}.webmanifest` lets WebAPK minting
 * succeed and produces a proper installable PWA with the tenant's brand.
 * We pass `?origin=<storefront origin>&basename=<router basename>` so the
 * backend can emit absolute `start_url`/`scope` pointing at the page
 * origin rather than the gateway.
 */

import { API_BASE } from "@/api/http";
import type { OnlineOrderingConfig } from "@/api/online-ordering-service/model";
import { storefrontName } from "@/lib/storefrontName";

const MANIFEST_LINK_ID = "app-manifest";
const APPLE_ICON_LINK_ID = "app-apple-touch-icon";
const FAVICON_LINK_ID = "app-favicon";
const FAVICON_16_LINK_ID = "app-favicon-16";
const APPLE_TITLE_META_ID = "app-apple-title";
const THEME_COLOR_META_ID = "app-theme-color";

/** Track the input we last applied so noisy re-renders no-op. */
let lastAppliedKey: string | null = null;

const DEFAULT_PRIMARY = "#0baa57"; // Matches the static fallback in index.html.

function isHexColor(s: string | null | undefined): s is string {
  if (!s) return false;
  const hex = s.trim().replace(/^#/, "");
  return /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(hex);
}

function normalizeHex(s: string): string {
  const hex = s.trim().replace(/^#/, "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  return `#${full.toLowerCase()}`;
}

function setMetaContent(id: string, content: string): void {
  const el = document.getElementById(id) as HTMLMetaElement | null;
  if (el) el.content = content;
}

function setLinkHref(id: string, href: string): void {
  const el = document.getElementById(id) as HTMLLinkElement | null;
  if (el) el.href = href;
}

/**
 * Build the absolute URL of the per-tenant manifest endpoint.
 *
 * `API_BASE` may be:
 *   - `/api` in dev (proxied by Vite to the gateway) — the resulting URL
 *     is same-origin with the page, which is ideal for installability.
 *   - the staging gateway URL in production — cross-origin from the
 *     storefront, but that's fine because the backend emits absolute
 *     `start_url`/`scope` pointing back at `origin`.
 */
/**
 * URL of a backend-rendered tenant icon.
 *
 * `spec` is one of `192` / `512` / `maskable-192` / `maskable-512` /
 * `apple-180` (see `app/services/pwa_icons.py`). We use this instead of the raw
 * `logoUrl` because the upload is an arbitrary non-square image — iOS squashes
 * it into the home-screen tile and composites its transparency onto black.
 */
function buildIconUrl(slug: string, spec: string): string {
  return `${API_BASE}/v1/online-ordering/icon/${encodeURIComponent(
    slug,
  )}/${spec}.png`;
}

function buildManifestUrl(
  slug: string,
  basename: string,
  origin: string,
): string {
  const qs = new URLSearchParams();
  qs.set("origin", origin);
  if (basename) qs.set("basename", basename);
  // Cache-bust on tenant slug only — the backend already caches per-slug
  // for 5 minutes, which matches the dashboard's typical edit-to-preview
  // cycle.
  return `${API_BASE}/v1/online-ordering/manifest/${encodeURIComponent(
    slug,
  )}.webmanifest?${qs.toString()}`;
}

/**
 * Swap a `<link rel="manifest">` in-place. Some browsers (notably older
 * Safari/Chromium versions) only re-read the manifest after the link
 * element is removed and re-inserted, so we replace the node rather than
 * just mutating `.href`. We also set `crossorigin="anonymous"` because
 * the manifest is on a different origin in production and Chrome
 * requires explicit CORS opt-in for cross-origin manifests.
 */
function replaceManifestLink(href: string): void {
  const old = document.getElementById(MANIFEST_LINK_ID);
  const link = document.createElement("link");
  link.rel = "manifest";
  link.id = MANIFEST_LINK_ID;
  link.href = href;
  link.crossOrigin = "anonymous";
  if (old?.parentNode) {
    old.parentNode.replaceChild(link, old);
  } else {
    document.head.appendChild(link);
  }
}

/**
 * Apply the per-tenant manifest, icons, theme color, and document title.
 *
 * Synchronous now (no canvas / async image work) — Chrome can read the
 * manifest immediately, which matters because the install affordance
 * appears as soon as the manifest + service worker are both ready.
 *
 * @param config - The resolved tenant config (the same object stored in
 *   BranchProvider). Pass `null` to leave the static defaults in place.
 * @param basename - Router basename from `resolveSlug()` — `/<slug>` for
 *   path-based tenants, `""` for hostname-based (custom domains).
 * @param slug - The resolved tenant slug. Used in the manifest URL path.
 */
export function applyTenantPwa(
  config: OnlineOrderingConfig | null,
  basename: string,
  slug: string | null,
): void {
  if (typeof document === "undefined") return;
  if (!config || !slug) return;

  // `displayName` is the resolved storefront name — the merchant's own menu-link
  // name when they set one, else the brand. `config.name` is the raw link field
  // and is frequently a default ("Default", "Online Ordering"), which is what
  // installed apps were being labelled with.
  const name = storefrontName(config);
  const logoUrl =
    (config as { logoUrl?: string | null } | null)?.logoUrl ?? null;
  const primary =
    (config?.colorScheme?.primary &&
    isHexColor(config.colorScheme.primary)
      ? normalizeHex(config.colorScheme.primary)
      : null) ??
    ((config as { themeColor?: string | null } | null)?.themeColor &&
    isHexColor((config as { themeColor?: string | null }).themeColor)
      ? normalizeHex(
          (config as { themeColor?: string | null }).themeColor as string,
        )
      : null) ??
    DEFAULT_PRIMARY;

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";

  // Dedupe — BranchProvider's effect can fire on unrelated re-renders.
  const key = `${slug}|${name}|${logoUrl ?? ""}|${primary}|${basename}|${origin}`;
  if (key === lastAppliedKey) return;
  lastAppliedKey = key;

  // 1. Document title — `<Brand> — Online Ordering`.
  document.title = `${name} — Online Ordering`;

  // 2. Theme color meta (Android Chrome status bar tint).
  setMetaContent(THEME_COLOR_META_ID, primary);

  // 3. apple-mobile-web-app-title — the label under the iOS home-screen icon.
  setMetaContent(APPLE_TITLE_META_ID, name);

  // 4. apple-touch-icon. iOS reads the link DIRECTLY from the DOM at
  //    add-to-home-screen time and does not look at the manifest. Point it at
  //    the backend resize route rather than the raw logo: iOS does not letterbox
  //    a non-square image (it stretches it into the tile) and composites any
  //    transparency onto black. `apple-180` returns a square, opaque,
  //    aspect-preserved 180x180 PNG. Falls back to the static default when the
  //    tenant has no logo (the route 404s in that case).
  setLinkHref(
    APPLE_ICON_LINK_ID,
    logoUrl ? buildIconUrl(slug, "apple-180") : "/apple-touch-icon.png",
  );

  // 4b. Browser-tab favicons. index.html has carried an `id` on the 32px link
  //     since this module was written, but nothing ever used it, so every
  //     tenant's tab showed the Tappya "T" no matter whose storefront it was.
  setLinkHref(
    FAVICON_LINK_ID,
    logoUrl ? buildIconUrl(slug, "favicon-32") : "/favicon-32.png",
  );
  setLinkHref(
    FAVICON_16_LINK_ID,
    logoUrl ? buildIconUrl(slug, "favicon-16") : "/favicon-16.png",
  );

  // 5. Swap the manifest <link> to point at the per-tenant backend
  //    endpoint. The backend reads the same MenuLink the config endpoint
  //    does and returns proper application/manifest+json with absolute
  //    `start_url`/`scope` derived from `origin` + `basename`.
  const manifestUrl = buildManifestUrl(slug, basename, origin);
  replaceManifestLink(manifestUrl);
}
